import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { UserPlus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import TemporaryDrawer from '@/components/TemporaryDrawer';
import { useConfirm } from '@/hooks/useConfirm';

import { tiersQueryKeys, useTiersQuery } from '../../tiers/hooks/useTiersQuery';
import { tierUsersQueryKeys } from '../../tiers/hooks/useTierUsersQuery';
import { tierApprovalsService } from '../../tiers/infra/tierApprovalsService';
import { type AffiliateIdentifierType, IDENTIFIER_TYPE_OPTIONS } from '../domain/affiliateIdentifier';
import {
  type AffiliateRegion,
  type AffiliateStatus,
  type CreateAffiliateDto,
  REGION_OPTIONS,
  type TierProtection,
} from '../domain/types';
import { useCreateAffiliate } from '../hooks/useCreateAffiliate';
import { AffiliateForm, type AffiliateFormValues } from './AffiliateForm';
import { type PendingProtection } from './TierProtectionCard';

interface CreateAffiliateModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
}

const validationSchema = Yup.object({
  user_identifier: Yup.string().required('This field is required'),
  user_identifier_type: Yup.string()
    .oneOf(IDENTIFIER_TYPE_OPTIONS.map((option) => option.value))
    .required('Identifier type is required'),
  alias: Yup.string().optional(),
  region: Yup.string()
    .oneOf([...REGION_OPTIONS, ''], 'Invalid region')
    .optional(),
  status: Yup.string().optional(),
  note: Yup.string().optional(),
});

export function CreateAffiliateModal({ open, onClose, projectId }: CreateAffiliateModalProps) {
  const { createAffiliateAsync, isLoading, isSuccess } = useCreateAffiliate();
  const prevIsSuccess = useRef(isSuccess);
  const { data: tiers = [] } = useTiersQuery();
  const queryClient = useQueryClient();
  const sortedTiers = useMemo(() => [...tiers].sort((a, b) => a.rank - b.rank), [tiers]);
  const defaultTier = useMemo(() => {
    const found = tiers.find((t) => t.audienceId === null);
    return found ? { id: found.id, name: found.name, slug: found.slug, rank: found.rank } : null;
  }, [tiers]);
  const emptyProtections = useMemo<TierProtection[]>(() => [], []);
  const [pendingProtection, setPendingProtection] = useState<PendingProtection | null>(null);
  const [protectionIncomplete, setProtectionIncomplete] = useState(false);
  const [ConfirmDialog, confirm] = useConfirm('warning');

  const handlePendingChange = useCallback(
    (pending: PendingProtection | null, _clearing: boolean, incomplete: boolean) => {
      setPendingProtection(pending);
      setProtectionIncomplete(incomplete);
    },
    [],
  );

  const formik = useFormik<AffiliateFormValues>({
    initialValues: {
      user_identifier: '',
      user_identifier_type: 'evm_address' as AffiliateIdentifierType,
      alias: '',
      region: '',
      status: 'Active' as string,
      note: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const pendingTier = pendingProtection ? sortedTiers.find((t) => t.id === pendingProtection.tier_id) : null;
      const requiresApproval = !!pendingTier?.requiresManualProjectApproval;

      if (requiresApproval && pendingTier) {
        const confirmed = await confirm({
          title: 'Approve and assign tier protection',
          message: `${pendingTier.name} requires manual approval. Saving will assign the affiliate to this tier and approve them automatically. Continue?`,
          confirmButtonText: 'Approve and save',
          cancelButtonText: 'Cancel',
        });
        if (!confirmed) return;
      }

      const body: CreateAffiliateDto = {
        user_identifier: values.user_identifier ?? '',
        user_identifier_type: values.user_identifier_type as CreateAffiliateDto['user_identifier_type'],
        alias: values.alias || undefined,
        region: (values.region as AffiliateRegion) || undefined,
        status: (values.status as AffiliateStatus) || undefined,
        tier_protection_tier_id: pendingProtection?.tier_id,
        tier_protection_days: pendingProtection?.days,
        note: values.note || undefined,
      };

      try {
        await createAffiliateAsync({ projectId, body });
      } catch {
        // Toast already surfaced by the hook's onError
        return;
      }

      if (requiresApproval && pendingProtection) {
        try {
          await tierApprovalsService.approve(projectId, {
            project_tier_id: pendingProtection.tier_id,
            user_identifier: body.user_identifier,
            user_identifier_type: body.user_identifier_type,
          });
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: tiersQueryKeys.all(projectId) }),
            queryClient.invalidateQueries({ queryKey: tierUsersQueryKeys.all(projectId, pendingProtection.tier_id) }),
          ]);
        } catch {
          toast.error('Affiliate created, but approval failed. You can approve from the tier page.');
        }
      }
    },
  });

  useEffect(() => {
    if (isSuccess && !prevIsSuccess.current) {
      formik.resetForm();
      setPendingProtection(null);
      onClose();
    }
    prevIsSuccess.current = isSuccess;
  }, [isSuccess, formik, onClose]);

  const handleClose = () => {
    formik.resetForm();
    setPendingProtection(null);
    onClose();
  };

  return (
    <TemporaryDrawer
      open={open}
      onClose={handleClose}
      title="Add Affiliate"
      subtitle="Register a new affiliate to this project"
      icon={<UserPlus size={20} />}
      shouldConfirmClose={formik.dirty || pendingProtection !== null}
    >
      <AffiliateForm
        mode="create"
        projectId={projectId}
        formik={formik}
        organicTier={null}
        currentTier={defaultTier}
        protections={emptyProtections}
        tiers={sortedTiers}
        onPendingProtectionChange={handlePendingChange}
        onCancel={handleClose}
        submitLabel="Add Affiliate"
        submittingLabel="Creating..."
        isSubmitting={isLoading}
        isProtectionLoading={isLoading}
        submitDisabled={protectionIncomplete}
      />
      {ConfirmDialog}
    </TemporaryDrawer>
  );
}
